import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  ListChecks, Plus, Pencil, Trash2, Download, RefreshCw, Filter,
  CheckCircle2, AlertTriangle, Clock, FileSpreadsheet, ShieldCheck, X as XIcon,
} from "lucide-react";
import { toast } from "sonner";

const RECURRENCE_LABELS = {
  daily: "Daily",
  weekly: "Weekly",
  fortnightly_15: "Fortnightly (15-day)",
  monthly: "Monthly",
  quarterly: "Quarterly",
  half_yearly: "Half-yearly",
  yearly: "Yearly",
  once: "Once (one-time task)",
};
const CATEGORIES = ["Statutory", "Safety", "Admin", "Finance", "HR", "Other"];

const CAN_WRITE = ["super_admin", "mis", "hr", "ea"];
const CAN_DELETE = ["super_admin", "mis"];
const APPROVAL_VIEWERS = ["super_admin", "mis", "senior_management", "hr", "ea"];
const APPROVERS = ["mis", "senior_management"];

export default function ChecklistAdmin() {
  const { user } = useAuth();
  const canWrite = CAN_WRITE.includes(user?.role);
  const canDelete = CAN_DELETE.includes(user?.role);
  const showApprovalTab = APPROVAL_VIEWERS.includes(user?.role);
  const [tab, setTab] = useState("templates");

  return (
    <div data-testid="checklist-admin-page" className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <ListChecks className="h-7 w-7 text-red-600 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 6 · Compliance</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Recurring Checklist
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Define statutory / safety / admin tasks that recur on a schedule. Each
            occurrence fans out to the assigned people, with working-day-aware due
            dates and automatic on-time / late / missed tracking.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="instances" data-testid="tab-instances">Instances</TabsTrigger>
          {showApprovalTab && (
            <TabsTrigger value="approvals" data-testid="tab-approvals">
              Approval Queue
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="templates" className="mt-4">
          <TemplatesTab canWrite={canWrite} canDelete={canDelete} />
        </TabsContent>
        <TabsContent value="instances" className="mt-4">
          <InstancesTab isAdmin={true} />
        </TabsContent>
        {showApprovalTab && (
          <TabsContent value="approvals" className="mt-4">
            <ApprovalQueueTab canApprove={APPROVERS.includes(user?.role)} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ===========================================================================
// TEMPLATES TAB
// ===========================================================================
function TemplatesTab({ canWrite, canDelete }) {
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [t, u] = await Promise.all([
        api.get("/checklist/templates"),
        api.get("/checklist/assignable-principals").catch(() => ({ data: [] })),
      ]);
      setTemplates(t.data || []);
      setUsers(u.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const usersById = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u])), [users],
  );

  const onSave = async (payload, editId) => {
    try {
      if (editId) {
        await api.patch(`/checklist/templates/${editId}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/checklist/templates", payload);
        toast.success("Template created");
      }
      setDialogOpen(false);
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const onDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/checklist/templates/${confirmDel.id}`);
      toast.success(`Deleted "${confirmDel.title}"`);
      setConfirmDel(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {templates.length} template{templates.length === 1 ? "" : "s"}
        </div>
        {canWrite && (
          <Button
            className="brand-bg brand-bg-hover text-white"
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="add-template-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New Template
          </Button>
        )}
      </div>

      {!loading && templates.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center"
             data-testid="templates-empty">
          <FileSpreadsheet className="h-10 w-10 text-slate-300 mx-auto" />
          <h3 className="mt-3 text-sm font-semibold text-slate-700">No templates yet</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            Create a template to define a recurring compliance task — e.g. monthly
            PF return, weekly safety walkthrough. The system will automatically
            create per-period instances for each assignee.
          </p>
        </div>
      )}

      {templates.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Category</th>
                <th className="text-left px-4 py-2">Recurrence</th>
                <th className="text-left px-4 py-2">Assignees</th>
                <th className="text-center px-4 py-2">Open / Total</th>
                <th className="text-center px-4 py-2">Status</th>
                <th className="text-right px-4 py-2 w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id} className="border-t border-slate-100" data-testid={`template-row-${t.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{t.title}</div>
                    {t.description && (
                      <div className="text-[11px] text-slate-500 truncate max-w-md">{t.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] bg-slate-100 px-2 py-0.5 rounded">{t.category}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {RECURRENCE_LABELS[t.recurrence]}
                    {t.shared && <span className="ml-2 text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">SHARED</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-700">
                    {(t.assignee_user_ids || []).map((id) => usersById[id]?.name || id).join(", ")}
                  </td>
                  <td className="px-4 py-3 text-center font-mono text-xs">
                    <span className={t.open_count > 0 ? "text-amber-700 font-semibold" : "text-slate-400"}>
                      {t.open_count ?? 0}
                    </span>
                    <span className="text-slate-400"> / {t.instance_count ?? 0}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {t.active ? (
                      <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">Active</span>
                    ) : (
                      <span className="text-[10px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">Paused</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canWrite && (
                      <Button variant="ghost" size="sm"
                              data-testid={`template-edit-${t.id}`}
                              onClick={() => { setEditing(t); setDialogOpen(true); }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button variant="ghost" size="sm" className="text-red-600"
                              data-testid={`template-delete-${t.id}`}
                              onClick={() => setConfirmDel(t)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TemplateDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        users={users}
        editing={editing}
        onSave={onSave}
      />
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete template?</DialogTitle>
            <DialogDescription>
              This deletes <b>{confirmDel?.title}</b> and all of its open
              (not-completed) instances. Completed instances are kept for audit.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button variant="destructive" data-testid="confirm-delete-btn" onClick={onDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateDialog({ open, onClose, users, editing, onSave }) {
  const blank = {
    title: "", description: "", category: "Other",
    assignee_user_ids: [], shared: false,
    start_date: new Date().toISOString().slice(0, 10),
    recurrence: "monthly", attachment_required: false,
    lead_days: 2, grace_days: 0, include_weekends: false, active: true,
  };
  const [form, setForm] = useState(blank);

  useEffect(() => {
    if (editing) {
      setForm({
        title: editing.title || "",
        description: editing.description || "",
        category: editing.category || "Other",
        assignee_user_ids: editing.assignee_user_ids || [],
        shared: !!editing.shared,
        start_date: editing.start_date || blank.start_date,
        recurrence: editing.recurrence || "monthly",
        attachment_required: !!editing.attachment_required,
        lead_days: editing.lead_days ?? 2,
        grace_days: editing.grace_days ?? 0,
        include_weekends: !!editing.include_weekends,
        active: editing.active !== false,
      });
    } else {
      setForm(blank);
    }
    // eslint-disable-next-line
  }, [editing, open]);

  const submit = () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.assignee_user_ids.length) return toast.error("Select at least one assignee");
    onSave({
      ...form,
      lead_days: Number(form.lead_days) || 0,
      grace_days: Number(form.grace_days) || 0,
    }, editing?.id);
  };

  const toggleAssignee = (uid) => {
    setForm((f) => ({
      ...f,
      assignee_user_ids: f.assignee_user_ids.includes(uid)
        ? f.assignee_user_ids.filter((x) => x !== uid)
        : [...f.assignee_user_ids, uid],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Template" : "New Checklist Template"}</DialogTitle>
          <DialogDescription>
            Set the recurrence + assignees. Working-day-aware due dates roll
            backward over Sundays / holidays automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   data-testid="template-title-input"
                   placeholder="e.g. Monthly PF Return" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      data-testid="template-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="template-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Recurrence</Label>
              <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                <SelectTrigger data-testid="template-recurrence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(RECURRENCE_LABELS).map(([k, l]) => (
                    <SelectItem key={k} value={k}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Start date *</Label>
              <Input type="date" value={form.start_date}
                     onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                     data-testid="template-start-date" />
            </div>
            <div>
              <Label className="text-xs">Lead days</Label>
              <Input type="number" min="0" max="30" value={form.lead_days}
                     onChange={(e) => setForm({ ...form, lead_days: e.target.value })}
                     data-testid="template-lead-days" />
            </div>
            <div>
              <Label className="text-xs">Grace days</Label>
              <Input type="number" min="0" max="30" value={form.grace_days}
                     onChange={(e) => setForm({ ...form, grace_days: e.target.value })}
                     data-testid="template-grace-days" />
            </div>
          </div>
          {form.recurrence === "daily" && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 flex items-center gap-2" data-testid="daily-weekend-row">
              <input
                type="checkbox"
                id="include-weekends"
                checked={!!form.include_weekends}
                onChange={(e) => setForm({ ...form, include_weekends: e.target.checked })}
                data-testid="template-include-weekends"
                className="h-4 w-4 accent-amber-600"
              />
              <label htmlFor="include-weekends" className="text-xs text-amber-900 cursor-pointer">
                Include weekends &amp; holidays
                <span className="text-[10px] text-amber-700 ml-1">
                  ({form.include_weekends ? "calendar daily — runs every day" : "working-day daily — skips Sun + holidays"})
                </span>
              </label>
            </div>
          )}
          {form.recurrence === "once" && (
            <div className="bg-sky-50 border border-sky-200 rounded-md p-2.5 text-[11px] text-sky-900" data-testid="once-hint">
              <b>Once</b> creates a single non-recurring task. Start date above is also the due date.
            </div>
          )}
          <AssigneePicker users={users}
                          selected={form.assignee_user_ids}
                          onToggle={toggleAssignee} />
          <div className="grid grid-cols-3 gap-3 pt-1">
            <Toggle label="Shared" testid="template-shared-switch"
                    checked={form.shared} onChange={(v) => setForm({ ...form, shared: v })} />
            <Toggle label="Attachment required" testid="template-attach-required-switch"
                    checked={form.attachment_required} onChange={(v) => setForm({ ...form, attachment_required: v })} />
            <Toggle label="Active" testid="template-active-switch"
                    checked={form.active} onChange={(v) => setForm({ ...form, active: v })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="brand-bg brand-bg-hover text-white"
                  data-testid="template-save-btn">
            {editing ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between border border-slate-200 rounded px-3 py-2">
      <Label className="text-xs">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function AssigneePicker({ users, selected, onToggle }) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(q)
      || (u.email || "").toLowerCase().includes(q)
      || (u.employee_code || "").toLowerCase().includes(q)
      || (u.role || "").toLowerCase().includes(q)
    );
  }, [users, search]);
  const userPrincipals = filtered.filter((u) => u.principal_type === "user");
  const employeePrincipals = filtered.filter((u) => u.principal_type === "employee");
  const selectedCount = selected.length;

  return (
    <div className="border border-slate-200 rounded p-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label className="text-xs">
          Assignees * <span className="text-slate-400">({selectedCount} selected)</span>
        </Label>
        <Input
          value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name / code / role..."
          className="h-7 text-xs w-56"
          data-testid="template-assignee-search"
        />
      </div>
      <div className="mt-2 max-h-72 overflow-y-auto space-y-1 pr-1">
        {filtered.length === 0 && (
          <div className="text-xs text-slate-400 px-2 py-3 text-center">
            No matching {search ? "results" : "users / employees"}.
          </div>
        )}

        {userPrincipals.length > 0 && (
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-2 mb-1 px-1">
            Login Users · {userPrincipals.length}
          </div>
        )}
        {userPrincipals.map((u) => (
          <AssigneeRow key={u.id} u={u} selected={selected.includes(u.id)}
                       onToggle={() => onToggle(u.id)} />
        ))}

        {employeePrincipals.length > 0 && (
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-3 mb-1 px-1">
            Employees · {employeePrincipals.length}
          </div>
        )}
        {employeePrincipals.map((u) => (
          <AssigneeRow key={u.id} u={u} selected={selected.includes(u.id)}
                       onToggle={() => onToggle(u.id)} />
        ))}
      </div>
    </div>
  );
}

function AssigneeRow({ u, selected, onToggle }) {
  const isEmp = u.principal_type === "employee";
  return (
    <label data-testid={`template-assignee-${u.id}`}
           className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded cursor-pointer
             ${selected ? "bg-red-50 border border-red-200" : "hover:bg-slate-50 border border-transparent"}`}>
      <input type="checkbox" checked={selected} onChange={onToggle} />
      <span className="font-medium truncate">{u.name}</span>
      {u.employee_code && (
        <span className="text-[10px] font-mono text-slate-400">{u.employee_code}</span>
      )}
      <span className="text-slate-400 truncate flex-1 min-w-0">{u.email}</span>
      <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded ${
        isEmp ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"
      }`}>{u.role}</span>
    </label>
  );
}

// ===========================================================================
// INSTANCES TAB
// ===========================================================================
export function InstancesTab({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "", category: "", overdue: "", from: "", to: "",
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      if (filters.from) params.from_date = filters.from;
      if (filters.to) params.to_date = filters.to;
      if (filters.overdue === "yes") params.overdue = true;
      if (filters.overdue === "no") params.overdue = false;
      const [{ data }, u] = await Promise.all([
        api.get("/checklist/instances", { params }),
        api.get("/checklist/assignable-principals").catch(() => ({ data: [] })),
      ]);
      setRows(data || []);
      setUsers(u.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filters]);

  const usersById = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u])), [users],
  );

  const onExport = async () => {
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      if (filters.from) params.from_date = filters.from;
      if (filters.to) params.to_date = filters.to;
      if (filters.overdue === "yes") params.overdue = true;
      if (filters.overdue === "no") params.overdue = false;
      const resp = await api.get("/checklist/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `checklist_register_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-end gap-2">
        <div className="flex items-center gap-1 text-xs text-slate-500 mr-2">
          <Filter className="h-3 w-3" /> Filters
        </div>
        <FilterSelect label="Status" value={filters.status}
                      onChange={(v) => setFilters({ ...filters, status: v })}
                      options={[["", "All"], ["not_started", "Not started"], ["in_progress", "In progress"], ["pending_approval", "Awaiting approval"], ["completed", "Completed"]]}
                      testid="filter-status" />
        <FilterSelect label="Category" value={filters.category}
                      onChange={(v) => setFilters({ ...filters, category: v })}
                      options={[["", "All"], ...CATEGORIES.map((c) => [c, c])]}
                      testid="filter-category" />
        <FilterSelect label="Overdue" value={filters.overdue}
                      onChange={(v) => setFilters({ ...filters, overdue: v })}
                      options={[["", "All"], ["yes", "Overdue only"], ["no", "Not overdue"]]}
                      testid="filter-overdue" />
        <div>
          <Label className="text-[10px]">From (effective)</Label>
          <Input type="date" value={filters.from} className="h-8 w-36"
                 onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
        </div>
        <div>
          <Label className="text-[10px]">To (effective)</Label>
          <Input type="date" value={filters.to} className="h-8 w-36"
                 onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={refresh} data-testid="instances-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={onExport} data-testid="instances-export-btn">
              <Download className="h-3.5 w-3.5 mr-1" /> Excel
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">Period</th>
              <th className="text-left px-3 py-2">Assignee</th>
              <th className="text-left px-3 py-2">Due (effective)</th>
              <th className="text-left px-3 py-2">Timeliness</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="text-center px-4 py-10 text-xs text-slate-400">
                No instances match your filters.
              </td></tr>
            )}
            {rows.map((r) => (
              <InstanceRow key={r.id} r={r} usersById={usersById} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InstanceRow({ r, usersById }) {
  const u = r.assignee_user_id ? usersById[r.assignee_user_id] : null;
  const tone = r.overdue ? "red"
    : r.status === "completed" ? "green"
    : r.status === "pending_approval" ? "violet"
    : r.status === "in_progress" ? "amber" : "slate";
  const dotCls = {
    red: "bg-red-500",
    green: "bg-emerald-500",
    violet: "bg-violet-500",
    amber: "bg-amber-500",
    slate: "bg-slate-300",
  }[tone];
  const label = r.status === "pending_approval" ? "awaiting approval"
    : (r.status || "").replace("_", " ");
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`instance-row-${r.id}`}>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${dotCls}`} data-testid={`instance-status-${r.id}`} />
          <span className="text-xs capitalize">{label}</span>
        </div>
      </td>
      <td className="px-3 py-2.5">
        <div className="font-medium text-slate-800 text-sm">{r.template_title}</div>
        <div className="text-[10px] text-slate-400">{r.category}</div>
      </td>
      <td className="px-3 py-2.5 font-mono text-xs">{r.period_label}</td>
      <td className="px-3 py-2.5 text-xs text-slate-700">
        {r.shared ? <em>Shared</em> : (u?.name || r.assignee_user_id || "—")}
      </td>
      <td className="px-3 py-2.5 text-xs">
        <div className="text-slate-800">{r.effective_due_date}</div>
        {r.nominal_due_date !== r.effective_due_date && (
          <div className="text-[10px] text-slate-400">nominal: {r.nominal_due_date}</div>
        )}
        {r.active_from && r.active_from > new Date().toISOString().slice(0, 10) && (
          <div className="text-[10px] text-amber-600 mt-0.5" title="This instance is scheduled — opens for action on this date">
            Opens {r.active_from}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5">
        <TimelinessBadge inst={r} />
      </td>
    </tr>
  );
}

function TimelinessBadge({ inst }) {
  if (inst.timeliness === "on_time") {
    return <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
      <CheckCircle2 className="h-3 w-3" /> On time
    </span>;
  }
  if (inst.timeliness === "late") {
    return <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
      Late ({inst.negative_mark})
    </span>;
  }
  if (inst.timeliness === "missed" || inst.overdue) {
    return <span className="inline-flex items-center gap-1 text-[11px] text-red-800 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
      <AlertTriangle className="h-3 w-3" /> Missed ({inst.negative_mark})
    </span>;
  }
  return <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
    <Clock className="h-3 w-3" /> In window
  </span>;
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-[10px]">{label}</Label>
      <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
        <SelectTrigger className="h-8 w-36 text-xs" data-testid={testid}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(([v, l]) => (
            <SelectItem key={v || "__all"} value={v || "__all"}>{l}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}


// ===========================================================================
// APPROVAL QUEUE TAB
// ===========================================================================
function ApprovalQueueTab({ canApprove }) {
  const [data, setData] = useState({ rows: [], count: 0, can_approve: canApprove });
  const [loading, setLoading] = useState(true);
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/checklist/approval-queue");
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const onApprove = async (id) => {
    try {
      await api.post(`/checklist/instances/${id}/approve`);
      toast.success("Approved");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Approve failed");
    }
  };

  const onReject = async () => {
    if (!rejectFor) return;
    if (!reason.trim()) return toast.error("Reason is required");
    try {
      await api.post(`/checklist/instances/${rejectFor.id}/reject`, { reason });
      toast.success("Rejected — sent back to assignee");
      setRejectFor(null);
      setReason("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reject failed");
    }
  };

  const rows = data.rows || [];
  return (
    <div className="space-y-3" data-testid="approval-queue-tab">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <div>
          <ShieldCheck className="inline h-3 w-3 mr-1 text-violet-600" />
          {rows.length} submission{rows.length === 1 ? "" : "s"} awaiting approval
        </div>
        {!data.can_approve && (
          <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
            View-only — only MIS / Senior Management can approve
          </span>
        )}
      </div>

      {!loading && rows.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center"
             data-testid="approval-queue-empty">
          <ShieldCheck className="h-10 w-10 text-emerald-300 mx-auto" />
          <h3 className="mt-3 text-sm font-semibold text-slate-700">All clear!</h3>
          <p className="text-xs text-slate-500">No submissions are waiting for approval.</p>
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id}
               data-testid={`approval-row-${r.id}`}
               className="bg-white border border-violet-200 rounded-lg p-4">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-slate-900 text-sm">{r.template_title}</h3>
                  <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{r.category}</span>
                  <span className="text-[10px] font-mono text-slate-500">{r.period_label}</span>
                  {r.timeliness === "late" && (
                    <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Late submission</span>
                  )}
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  Submitted by <b>{r.submitted_by_name || "—"}</b>
                  <span className="text-slate-400">
                    {" "}· {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : ""}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Assignee: <b>{r.assignee_name || "—"}</b>
                  {r.assignee_employee_code && (
                    <span className="font-mono ml-1 text-slate-400">{r.assignee_employee_code}</span>
                  )}
                  {" "}· Effective due: {r.effective_due_date}
                </div>
                {(r.attachment_urls || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {r.attachment_urls.map((a, i) => (
                      <a key={i} href={a.url} target="_blank" rel="noreferrer"
                         className="text-[11px] underline text-red-700">
                        📎 {a.name || `file-${i + 1}`}
                      </a>
                    ))}
                  </div>
                )}
                {r.submission_note && (
                  <div className="text-[11px] text-slate-700 mt-2 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                    <b>Comment from {r.submitted_by_name || "submitter"}:</b> {r.submission_note}
                  </div>
                )}
              </div>
              {data.can_approve && (
                <div className="flex flex-col gap-1.5">
                  <Button size="sm" className="brand-bg brand-bg-hover text-white"
                          onClick={() => onApprove(r.id)}
                          data-testid={`approve-btn-${r.id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200"
                          onClick={() => { setRejectFor(r); setReason(""); }}
                          data-testid={`reject-btn-${r.id}`}>
                    <XIcon className="h-3 w-3 mr-1" /> Reject
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject submission?</DialogTitle>
            <DialogDescription>
              Send <b>{rejectFor?.template_title}</b> back to <b>{rejectFor?.assignee_name}</b>.
              They'll see your note and can re-submit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Reason *</Label>
            <Textarea rows={3} value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      data-testid="reject-reason-input"
                      placeholder="What needs to be fixed?" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button variant="destructive" onClick={onReject}
                    data-testid="reject-confirm-btn">Send back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
